import { t } from "i18next";
import { match } from "ts-pattern";
import { z } from "zod";

import { FieldBuilder, MultiPageFormBuilder } from "../../formBuilder/index.js";
import {
  givenHealthCareProfessionalAdviceSchema,
  IncidentTypeSchema,
  longTextSchema,
  nhsContactResults,
  nhsContactSchema,
  shortTextSchema,
  triageOutcomeSchema,
  voluntaryActionSchema,
  yesNoSchema,
  yesOnlySchema,
} from "../../types/index.js";
import { tk } from "../../types/Translation.js";
import type { TranslationKey } from "../../types/Translation.js";

/**
 * Every reason an officer could have recorded, which is wider than the set the
 * form offers. A reason stays here once it has been offered, because an
 * incident's answers have to stay readable for the life of the record.
 */
export const reasonNotConsultedSchema = z.enum([
  "noAnswer",
  "violent",
  "immediateRestraint",
  "singleCrewed",
  "poorConnectivity",
  "other",
  "immediateNeedForCareOrControl",
  "didNotConsider",
  "notSureWhoToContact",
  "notDeemedNecessary",
]);

export const formSpecification = new MultiPageFormBuilder()
  .withFieldAlwaysVisible({
    fieldName: "isHealthcareProfessionalConsulted", // triage-1.0
    type: "radio",
    ...new FieldBuilder().withSchema(yesNoSchema).captureValueLabel().build(),
    label: tk("triageIncident.isHealthcareProfessionalConsulted"),
    options: [
      {
        label: tk("common.yes"),
        value: "yes",
      },
      {
        label: tk("common.no"),
        value: "no",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "howDidYouContactNhs", // triage-1.0.3
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(nhsContactSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "yes",
    label: tk("triageIncident.howDidYouContactNhs.title"),
    options: [
      {
        value: "healthcarePractitionerAmbulance", // triage-1.0.3.1
        label: tk(
          "triageIncident.howDidYouContactNhs.options.healthcarePractitionerAmbulance",
        ),
      },
      {
        value: "custodyHealthcarePractitioner", // triage-1.0.3.3
        label: tk(
          "triageIncident.howDidYouContactNhs.options.custodyHealthcarePractitioner",
        ),
      },
      {
        value: "panLondonMentalHealthAdviceLine", // triage-1.0.3.4
        label: tk(
          "triageIncident.howDidYouContactNhs.options.panLondonMentalHealthAdviceLine",
        ),
      },
      {
        value: "localHbpos", // triage-1.0.3.6
        label: tk("triageIncident.howDidYouContactNhs.options.localHbpos"),
      },
      {
        value: "mentalHealthTeamOther", // triage-1.0.3.8
        label: tk(
          "triageIncident.howDidYouContactNhs.options.mentalHealthTeamOther",
        ),
      },
      {
        value: "triageCarHO", // triage-1.0.3.9
        label: tk("triageIncident.howDidYouContactNhs.options.triageCarHO"),
      },
      {
        value: "triageCarBtp", // triage-1.0.3.10
        label: tk("triageIncident.howDidYouContactNhs.options.triageCarBtp"),
      },
      {
        value: "nhs111", // triage-1.0.3.11
        label: tk("triageIncident.howDidYouContactNhs.options.nhs111"),
      },
      {
        value: "nhsStaffNwr", // triage-1.0.3.12
        label: tk("triageIncident.howDidYouContactNhs.options.nhsStaffNwr"),
      },
      {
        value: "nhsStaffAE", // triage-1.0.3.13
        label: tk("triageIncident.howDidYouContactNhs.options.nhsStaffAE"),
      },
      {
        value: "controlRoom", // triage-1.0.3.14
        label: tk("triageIncident.howDidYouContactNhs.options.controlRoom"),
      },
      {
        value: "amph", // triage-1.0.3.15
        label: tk("triageIncident.howDidYouContactNhs.options.amph"),
      },
      {
        value: "other", // triage-1.0.3.16
        label: tk("common.other"),
      },
    ] satisfies Array<{
      value: (typeof nhsContactResults)[number];
      label: TranslationKey;
    }>,
  })
  .withFieldConditionallyVisible({
    fieldName: "howDidYouContactNhsOtherDetails", // triage-1.0.3.16.1
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.howDidYouContactNhs?.value === "other",
    label: tk("triageIncident.provideDetails"),
  })
  .withFieldConditionallyVisible({
    fieldName: "reasonNotConsulted", // triage-1.0.1
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(reasonNotConsultedSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "no",
    label: tk("triageIncident.reasonNotConsulted.title"),
    options: [
      {
        label: tk("triageIncident.reasonNotConsulted.options.noAnswer"),
        value: "noAnswer",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.violent"),
        value: "violent",
      },
      {
        label: tk(
          "triageIncident.reasonNotConsulted.options.immediateRestraint",
        ),
        value: "immediateRestraint",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.singleCrewed"),
        value: "singleCrewed",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.poorConnectivity"),
        value: "poorConnectivity",
      },
      {
        label: tk("triageIncident.reasonNotConsulted.options.other"),
        value: "other",
      },
    ] satisfies Array<{
      value: z.infer<typeof reasonNotConsultedSchema>;
      label: TranslationKey;
    }>,
  })
  .withFieldConditionallyVisible({
    fieldName: "reasonNotConsultedOther", // triage-1.0.1.1.1
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.reasonNotConsulted?.value === "other",
    label: tk("triageIncident.reasonNotConsultedOther"),
  })
  .withFieldConditionallyVisible({
    fieldName: "givenHealthCareProfessionalAdvice", // triage-1.0.4
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(givenHealthCareProfessionalAdviceSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.isHealthcareProfessionalConsulted?.value === "yes",
    label: tk("triageIncident.givenHealthCareProfessionalAdvice"),
    options: [
      {
        label: tk("triageIncident.whatWillYouDo.136"),
        value: IncidentTypeSchema.enum.section136,
      },
      {
        label: tk("triageIncident.whatWillYouDo.56"),
        value: IncidentTypeSchema.enum.mentalCapacityAct,
      },
      {
        label: tk("triageIncident.whatWillYouDo.voluntary"),
        value: IncidentTypeSchema.enum.voluntary,
      },
      {
        label: tk("common.other"),
        value: IncidentTypeSchema.enum.other,
      },
    ],
  })
  /**
   * Declared but never shown. Writing a section replaces it outright, so a
   * field the schema does not name is dropped; a question the form has stopped
   * asking stays declared to keep the answers already recorded against it.
   */
  .withFieldNeverVisible({
    fieldName: "reasonsForDetention", // triage-1.0.4.1.1
    ...new FieldBuilder().withNullableSchema(longTextSchema).build(),
  })
  .withFieldConditionallyVisible({
    fieldName: "confirmMentalCapacityLack", // triage-1.0.4.1.2
    type: "checkbox",
    ...new FieldBuilder().withSchema(yesOnlySchema).captureValueLabel().build(),
    isVisible: ({ formValues }) =>
      formValues.givenHealthCareProfessionalAdvice?.value ===
      IncidentTypeSchema.enum.mentalCapacityAct,
    label: tk("triageIncident.confirmMentalCapacityLack"),
  })
  .withFieldConditionallyVisible({
    fieldName: "voluntaryAction", // triage-1.0.4.1.3
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(voluntaryActionSchema)
      .captureValueLabel()
      .build(),
    isVisible: ({ formValues }) =>
      formValues.givenHealthCareProfessionalAdvice?.value ===
      IncidentTypeSchema.enum.voluntary,
    label: tk("triageIncident.voluntaryServicesRecommended"),
    options: [
      {
        label: tk(
          "triageIncident.voluntaryAction.options.voluntaryAttendanceHospital",
        ),
        value: "voluntaryAttendanceHospital",
      },
      {
        label: tk(
          "triageIncident.voluntaryAction.options.divertOtherHealthservice",
        ),
        value: "divertOtherHealthservice",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.contactSupportGroup"),
        value: "contactSupportGroup",
      },
      {
        label: tk(
          "triageIncident.voluntaryAction.options.contactFriendsFamily",
        ),
        value: "contactFriendsFamily",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.takePersonHome"),
        value: "takePersonHome",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.divertCrisisCafe"),
        value: "divertCrisisCafe",
      },
      {
        label: tk("triageIncident.voluntaryAction.options.other"),
        value: "other",
      },
    ],
  })
  .withFieldConditionallyVisible({
    fieldName: "voluntaryActionOtherDetails", // triage-1.0.4.1.3.1.1
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.voluntaryAction?.value === "other",
    label: tk("triageIncident.provideDetails"),
    multiline: true,
    rows: "5",
  })
  .withFieldConditionallyVisible({
    fieldName: "otherActionDetails", // triage-1.0.4.1.4
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.givenHealthCareProfessionalAdvice?.value ===
      IncidentTypeSchema.enum.other,
    label: tk("triageIncident.otherActionDetails"),
    multiline: true,
    rows: "5",
  })
  .withFieldAlwaysVisible({
    fieldName: "whatWillYouDo", // triage-1.0.2
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(triageOutcomeSchema)
      .captureValueLabel()
      .build(),
    label: tk("triageIncident.whatActionWillYouTake"),
    options: ({ user }) => [
      {
        label: tk("triageIncident.whatWillYouDo.136"),
        value: IncidentTypeSchema.enum.section136,
      },
      ...(user?.sessionOrganisationConfiguration?.mentalCapacityActEnabled
        ? [
            {
              label: tk("triageIncident.whatWillYouDo.56"),
              value: IncidentTypeSchema.enum.mentalCapacityAct,
            } as const,
          ]
        : []),
      {
        label: tk("triageIncident.whatWillYouDo.voluntary"),
        value: IncidentTypeSchema.enum.voluntary,
      },
      {
        label: tk("triageIncident.whatWillYouDo.other"),
        value: IncidentTypeSchema.enum.other,
      },
    ],
    isEditable: ({ initialValue }) => {
      return initialValue === null;
    },
  })
  .withFieldConditionallyVisible({
    fieldName: "whatWillYouDoOther", // triage-1.0.2.1.1
    type: "textbox",
    ...new FieldBuilder().withSchema(longTextSchema).build(),
    isVisible: ({ formValues }) => formValues.whatWillYouDo?.value === "other",
    label: tk("triageIncident.provideDetails"),
    multiline: true,
    rows: "5",
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "otherActionBanner", // triage-1.0.4.1.4.1.2
    type: "banner",
    bannerType: "warning",
    body: tk("triageIncident.otherActionBanner"),
    isVisible: ({ formValues }) => formValues.whatWillYouDo?.value === "other",
  })
  .withFieldConditionallyVisible({
    fieldName: "explainWhyActionDiffers", // triage-1.0.5.2
    type: "dropdown",
    ...new FieldBuilder()
      .withSchema(
        z.enum([
          "changeInPresentation",
          "differenceInOpinion",
          "other",
        ] as const),
      )
      .captureValueLabel()
      .build(),
    options: [
      {
        label: tk(
          "triageIncident.explainWhyActionDiffers.options.changeInPresentation",
        ),
        value: "changeInPresentation",
      },
      {
        label: tk(
          "triageIncident.explainWhyActionDiffers.options.differenceInOpinion",
        ),
        value: "differenceInOpinion",
      },
      {
        label: tk("triageIncident.explainWhyActionDiffers.options.other"),
        value: "other",
      },
    ],
    isVisible: ({ formValues }) =>
      formValues.whatWillYouDo?.value !== undefined &&
      formValues.givenHealthCareProfessionalAdvice?.value !== undefined &&
      formValues.whatWillYouDo.value !==
        formValues.givenHealthCareProfessionalAdvice.value,
    label: tk("triageIncident.explainWhyActionDiffers.title"),
  })
  .withFieldConditionallyVisible({
    fieldName: "explainWhyActionDiffersOther", // triage-1.0.5.2.1.1
    type: "textbox",
    ...new FieldBuilder().withSchema(shortTextSchema).build(),
    isVisible: ({ formValues }) =>
      formValues.explainWhyActionDiffers?.value === "other",
    label: tk("triageIncident.explainWhyActionDiffersOther"),
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "mentalCapacityActCautionBanner",
    type: "banner",
    bannerType: "info",
    body: tk("triageIncident.mentalCapacityActCautionBanner"),
    isVisible: ({ formValues }) =>
      formValues.whatWillYouDo?.value ===
      IncidentTypeSchema.enum.mentalCapacityAct,
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "voluntaryNotDigitalisedBanner", // triage-1.0.2.2.1
    type: "banner",
    bannerType: "warning",
    // The outcome is recorded here so the incident can be closed and counted,
    // while the section 131 form it leads to is completed on paper.
    body: tk("triageIncident.voluntaryNotDigitalisedBanner"),
    isVisible: ({ formValues }) =>
      formValues.whatWillYouDo?.value === IncidentTypeSchema.enum.voluntary,
  })
  .withReadonlyFieldConditionallyVisible({
    fieldName: "unsupportedAction",
    type: "banner",
    bannerType: "error",
    body: tk("triageIncident.unsupportedAction"),
    // Names the outcomes this platform cannot yet carry, so every outcome has
    // to be classified as supported or not before it can be offered.
    isVisible: ({ formValues }) =>
      match(formValues.whatWillYouDo?.value)
        .with(IncidentTypeSchema.enum.other, () => true)
        .with(
          IncidentTypeSchema.enum.section136,
          IncidentTypeSchema.enum.mentalCapacityAct,
          IncidentTypeSchema.enum.voluntary,
          undefined,
          () => false,
        )
        .exhaustive(),
  })
  .withPage([
    "isHealthcareProfessionalConsulted", // triage-1.0
  ])
  .withPage([
    "reasonNotConsulted", // triage-1.0.1
    "reasonNotConsultedOther", // triage-1.0.1.1.1
    "howDidYouContactNhs", // triage-1.0.3
    "howDidYouContactNhsOtherDetails", // triage-1.0.3.16.1
  ])
  .withPage([
    "givenHealthCareProfessionalAdvice", // triage-1.0.4
    "confirmMentalCapacityLack", // triage-1.0.4.1.2
    "voluntaryAction", // triage-1.0.4.1.3
    "voluntaryActionOtherDetails", // triage-1.0.4.1.3.1.1
    "otherActionDetails", // triage-1.0.4.1.4
  ])
  .withPage([
    "whatWillYouDo", // triage-1.0.2
    "mentalCapacityActCautionBanner",
    "whatWillYouDoOther", // triage-1.0.2.1.1
    "explainWhyActionDiffers", // triage-1.0.5.2
    "explainWhyActionDiffersOther", // triage-1.0.5.2.1.1
    "otherActionBanner", // triage-1.0.4.1.4.1.2
    "voluntaryNotDigitalisedBanner", // triage-1.0.2.2.1
  ])
  .withPage(["unsupportedAction"])
  .build({ t });

export type TriageIncidentFormData = ReturnType<
  typeof formSpecification
>["formType"];
